// Fiche d'un bon de commande client : avancement par type, factures (ERP et reprises), BL rattachés.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, GlobalStyles, Grid, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  AccountBalanceWalletOutlined, AddOutlined, AssignmentOutlined, AttachFileOutlined, DeleteOutline, DescriptionOutlined, EditOutlined, LocalShippingOutlined, PrintOutlined,
  ReceiptLongOutlined, WarningAmberOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { marcheAPI } from '../../services/api';
import { apiError, dateFr, fcfa, BL_STATUS } from '../../utils/format';
import { tokens } from '../../theme';
import { Avancement, MARCHE_STATUT, typeLabel } from './common';

const N = ({ v, color, bold }) => <Box component="span" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', color, fontWeight: bold ? 700 : undefined }}>{v}</Box>;
const IconButtonLite = ({ label, onClick }) => (
  <Button size="small" color="error" aria-label={label} onClick={onClick} sx={{ minWidth: 0, p: 0.5 }}><DeleteOutline fontSize="small" /></Button>
);
const EXT_TYPES = { FACTURE: 'Facture antérieure', AVOIR: 'Avoir', ECART: 'Écart à justifier' };

const Marche = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [m, setM] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(() => marcheAPI.get(id).then((r) => setM(r.data)).catch((e) => setError(apiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError('');
    try { const r = await fn(); setDlg(null); setInfo(r.data.message); await load(); } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  const joindre = async (files) => {
    for (const file of Array.from(files || [])) {
      if (!/^(application\/pdf|image\/)/.test(file.type)) { setError(`« ${file.name} » : joignez un PDF ou une image`); continue; }
      if (file.size > 10 * 1024 * 1024) { setError(`« ${file.name} » dépasse 10 Mo`); continue; }
      // eslint-disable-next-line no-await-in-loop
      await run(() => marcheAPI.joindre(id, file));
    }
  };
  const ouvrirFichier = async (f) => {
    const w = window.open('', '_blank');
    try {
      const r = await marcheAPI.fichier(f.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: f.type }));
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) { if (w) w.close(); setError(apiError(e)); }
  };

  if (!m) return <Layout title="Bon de commande">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const t = m.totaux;
  const st = MARCHE_STATUT[m.statut];
  const ouvrirFacture = (f) => {
    if (f.source === 'ERP') setDlg({ type: 'validation', f, validation_dapa: f.validation_dapa || '', validation_compta: f.validation_compta || '' });
    else setDlg({ ...f, type: 'reprise', type_ext: f.type, f, montant_ht: String(Math.abs(f.montant_ht)), date_enlevement: f.date_enlevement || '', validation_dapa: f.validation_dapa || '',
      validation_compta: f.validation_compta || '', date_paiement: f.date_paiement || '', note: f.note || '' });
  };

  return (
    <Layout title={`BC${m.rang ? ` n°${m.rang}` : ''} — ${m.numero}`} plain
      actions={(
        <>
          <Button startIcon={<DescriptionOutlined />} onClick={() => navigate(`/marches/${id}/bc`)}>Bon de commande</Button>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer la situation</Button>
          {m.ecriture && <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => navigate(`/marches/${id}/modifier`)}>Modifier</Button>}
          {m.suppression?.droit && (
            <Button color="error" startIcon={<DeleteOutline />} title={m.suppression.obstacle || undefined}
              onClick={async () => {
                if (m.suppression.obstacle) { setError(`Suppression impossible — ${m.suppression.obstacle}`); return; }
                if (!window.confirm(`Supprimer définitivement le BC ${m.numero} (${m.client_nom}) et ses documents joints ?`)) return;
                try { const r = await marcheAPI.remove(id); navigate('/marches', { state: { info: r.data.message } }); } catch (e) { setError(apiError(e)); }
              }}>Supprimer</Button>
          )}
        </>
      )}>
      <GlobalStyles styles={{ '@media print': { 'nav, aside, .MuiDrawer-root, .no-print, button': { display: 'none !important' }, body: { background: '#fff !important' } } }} />
      {info && <Alert className="no-print" severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert className="no-print" severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Chip label={st.label} color={st.color} />
        {m.numero_docc && m.numero_docc !== m.numero && <Chip size="small" variant="outlined" label={`N° DOCC ${m.numero_docc}`} sx={{ fontFamily: tokens.mono }} />}
        <Typography color="text.secondary">{m.client_nom} · du {dateFr(m.date_bc)}{m.objet ? ` · ${m.objet}` : ''} · paiement à {m.delai_paiement_jours} j</Typography>
      </Box>
      {!m.lignes.length && (
        <Alert severity="warning" sx={{ mb: 2 }} className="no-print"
          action={m.ecriture && <Button color="inherit" variant="outlined" size="small" onClick={() => navigate(`/marches/${id}/modifier`)}>Compléter</Button>}>
          <b>BC à compléter</b> : déposé sans saisie{m.fichiers.length ? ' (voir le document ci-dessous)' : ''}. Saisissez les types, quantités et prix pour suivre le reste à livrer et facturer au prix du BC.
        </Alert>
      )}
      {m.alertes.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{m.alertes.map((a) => <div key={a}>{a}</div>)}</Alert>}
      {m.bl_non_factures.nombre > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} className="no-print"
          action={<Button color="inherit" onClick={() => navigate('/bons-livraison')}>Voir les BL</Button>}>
          {m.bl_non_factures.nombre} BL ({m.bl_non_factures.quantite} poteaux) rattaché(s) à ce BC ne sont pas encore facturés.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}><Kpi compact icon={AssignmentOutlined} tint="navy" label="Montant du BC" value={fcfa(t.commande_ht)} valueSize={20} sub={`${t.quantite} poteaux · F HT`} /></Grid>
        <Grid item xs={6} md={2.4}><Kpi compact icon={LocalShippingOutlined} tint="amber" label="Livré" value={`${t.livre} / ${t.quantite}`} valueSize={20} sub={`${t.avancement} % — reste ${t.reste_a_livrer}`} /></Grid>
        <Grid item xs={6} md={2.4}><Kpi compact icon={ReceiptLongOutlined} tint="gray" label="Facturé" value={fcfa(t.facture_ht)} valueSize={20} sub="F HT" /></Grid>
        <Grid item xs={6} md={2.4}><Kpi compact icon={AccountBalanceWalletOutlined} tint="green" label="Payé" value={fcfa(t.paye_ht)} valueSize={20} sub="F HT" /></Grid>
        <Grid item xs={12} md={2.4}>
          <Kpi compact icon={WarningAmberOutlined} tint="red" label="Non payé" value={fcfa(t.non_paye_ht)} valueSize={20} valueColor={t.echu_ht > 0 ? '#B4232A' : undefined} sub={`dont échu ${fcfa(t.echu_ht)} F`} />
        </Grid>
      </Grid>

      <Panel title="Situation par type de support" flush sx={{ mb: 2 }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Désignation</TableCell><TableCell align="right">Commandé</TableCell><TableCell align="right">Prix unitaire HT</TableCell>
                <TableCell align="right">Livré</TableCell><TableCell align="right">Facturé</TableCell><TableCell align="right">Reste à livrer</TableCell>
                <TableCell align="right">Reste à livrer HT</TableCell><TableCell>Avancement</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {m.lignes.map((l) => (
                <TableRow key={l.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{typeLabel(l.pba_type)}</TableCell>
                  <TableCell align="right"><N v={l.quantite} /></TableCell>
                  <TableCell align="right"><N v={fcfa(l.prix_unitaire_ht)} /></TableCell>
                  <TableCell align="right" title={l.reprise_livree ? `dont ${l.reprise_livree} avant l'ERP` : undefined}><N v={l.livre} /></TableCell>
                  <TableCell align="right" title={l.reprise_facturee ? `dont ${l.reprise_facturee} avant l'ERP` : undefined}>
                    <N v={l.facture} color={l.livre_non_facture > 0 ? '#8A5A0B' : undefined} />
                  </TableCell>
                  <TableCell align="right"><N v={l.reste_a_livrer} color={l.reste_a_livrer < 0 ? '#B4232A' : l.reste_a_livrer === 0 ? tokens.steel : undefined} bold={l.reste_a_livrer < 0} /></TableCell>
                  <TableCell align="right"><N v={fcfa(Math.max(0, l.reste_a_livrer) * l.prix_unitaire_ht)} /></TableCell>
                  <TableCell><Avancement value={l.quantite ? Math.round((100 * Math.min(l.livre, l.quantite)) / l.quantite) : 100} width={110} /></TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}` } }}>
                <TableCell>Total</TableCell><TableCell align="right"><N v={t.quantite} /></TableCell><TableCell />
                <TableCell align="right"><N v={t.livre} /></TableCell><TableCell align="right"><N v={m.lignes.reduce((s, l) => s + l.facture, 0)} /></TableCell>
                <TableCell align="right"><N v={t.reste_a_livrer} /></TableCell><TableCell align="right"><N v={fcfa(t.reste_a_livrer_ht)} /></TableCell><TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Panel title={`Factures (${m.factures.length})`} flush sx={{ mb: 2 }}
        action={m.ecriture && <Button size="small" className="no-print" startIcon={<AddOutlined />}
          onClick={() => setDlg({ type: 'reprise', f: null, type_ext: 'FACTURE', numero: '', montant_ht: '', date_enlevement: '', validation_dapa: '', validation_compta: '', date_paiement: '', note: '' })}>
          Facture antérieure / avoir
        </Button>}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Facture</TableCell><TableCell>Enlèvement</TableCell><TableCell>Validation DAPA</TableCell><TableCell>Validation compta.</TableCell>
                <TableCell>Échéance</TableCell><TableCell align="right">Montant HT</TableCell><TableCell align="right">Montant TTC</TableCell><TableCell>Situation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {m.reprise_paye_ht > 0 && (
                <TableRow sx={{ bgcolor: '#F7F8FA' }}>
                  <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Factures antérieures à l'ERP, payées</TableCell>
                  <TableCell align="right"><N v={fcfa(m.reprise_paye_ht)} /></TableCell><TableCell /><TableCell><Chip size="small" label="Payées" color="success" variant="outlined" /></TableCell>
                </TableRow>
              )}
              {m.factures.map((f) => (
                <TableRow key={`${f.source}-${f.id}`} hover sx={{ cursor: m.ecriture ? 'pointer' : 'default' }} onClick={() => m.ecriture && ouvrirFacture(f)}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography component="span" sx={{ fontFamily: tokens.mono, fontWeight: 600, fontSize: 14 }}>{f.numero}</Typography>
                    {f.source === 'REPRISE' && <Chip size="small" label={f.type === 'FACTURE' ? 'reprise' : EXT_TYPES[f.type]} variant="outlined" sx={{ ml: 0.75, height: 18, fontSize: 10.5 }} />}
                    {f.bls && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{f.bls}</Typography>}
                    {f.note && <Typography sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 320, whiteSpace: 'normal' }}>{f.note}</Typography>}
                  </TableCell>
                  <TableCell>{dateFr(f.date_enlevement)}</TableCell>
                  <TableCell>{dateFr(f.validation_dapa) || '—'}</TableCell>
                  <TableCell>{dateFr(f.validation_compta) || '—'}</TableCell>
                  <TableCell>{dateFr(f.echeance) || '—'}</TableCell>
                  <TableCell align="right"><N v={fcfa(f.montant_ht)} /></TableCell>
                  <TableCell align="right"><N v={fcfa(f.montant_ttc)} /></TableCell>
                  <TableCell>
                    {f.payee ? <Chip size="small" label={`Payée${f.date_paiement ? ` le ${dateFr(f.date_paiement)}` : ''}`} color="success" variant="outlined" />
                      : f.type === 'AVOIR' ? <Chip size="small" label="Avoir" color="warning" variant="outlined" />
                        : f.type === 'ECART' ? <Chip size="small" label="À justifier" color="warning" />
                          : f.echu ? <Chip size="small" label="Échu" color="error" /> : <Chip size="small" label="Non échu" variant="outlined" />}
                  </TableCell>
                </TableRow>
              ))}
              {!m.factures.length && !m.reprise_paye_ht && <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune facture.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {m.ecriture && <Typography className="no-print" sx={{ fontSize: 12.5, color: 'text.secondary', p: 1.5 }}>Cliquez sur une facture pour saisir ses dates de validation (DAPA, comptabilité) ou, pour une facture reprise, sa date de paiement. Les paiements des factures de l'ERP s'enregistrent sur la facture.</Typography>}
      </Panel>

      <Panel title={`Bons de livraison rattachés (${m.bls.length})`} flush sx={{ mb: 2 }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow><TableCell>BL</TableCell><TableCell>Date</TableCell><TableCell>Destination</TableCell><TableCell>Contenu</TableCell><TableCell>ADL</TableCell><TableCell>État</TableCell><TableCell>Facture</TableCell></TableRow></TableHead>
            <TableBody>
              {m.bls.map((b) => (
                <TableRow key={b.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/bons-livraison/${b.id}`)}>
                  <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{b.bl_numero}</TableCell>
                  <TableCell>{dateFr(b.delivery_date)}</TableCell><TableCell>{b.destination || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{b.contenu}</TableCell><TableCell>{b.adl || '—'}</TableCell>
                  <TableCell><Chip size="small" label={BL_STATUS[b.status]?.label || b.status} color={BL_STATUS[b.status]?.color} variant="outlined" /></TableCell>
                  <TableCell sx={{ fontFamily: tokens.mono }}>{b.invoice_numero || (b.invoice_id ? 'brouillon' : '—')}</TableCell>
                </TableRow>
              ))}
              {!m.bls.length && <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucun BL de l'ERP rattaché. Choisissez ce BC lors de la création du BL.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Box className="no-print"><Panel title={`Documents (${m.fichiers.length})`} sx={{ mb: 2 }}
        action={m.ecriture && m.fichiers.length < 10 && (
          <Button size="small" startIcon={<AttachFileOutlined />} onClick={() => fileInput.current?.click()}>Joindre le BC scanné</Button>
        )}>
        <input ref={fileInput} type="file" hidden multiple accept="application/pdf,image/*" data-testid="marche-fichier" onChange={(e) => { joindre(e.target.files); e.target.value = ''; }} />
        {m.fichiers.length ? m.fichiers.map((f) => (
          <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <AttachFileOutlined sx={{ fontSize: 18, color: tokens.steel }} />
            <Button size="small" sx={{ textTransform: 'none', fontWeight: 600, justifyContent: 'flex-start', minWidth: 0 }} onClick={() => ouvrirFichier(f)}>{f.nom}</Button>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flex: 1 }} noWrap>{Math.max(1, Math.round(f.taille / 1024))} Ko · {f.auteur || '—'} · {dateFr(String(f.created_at).slice(0, 10))}</Typography>
            {m.ecriture && <IconButtonLite label={`Retirer ${f.nom}`} onClick={() => window.confirm(`Retirer « ${f.nom} » ?`) && run(() => marcheAPI.retirerFichier(f.id))} />}
          </Box>
        )) : <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Aucun document. Joignez le bon de commande reçu du client (PDF ou photo).</Typography>}
      </Panel></Box>
      {m.notes && <Panel title="Notes"><Typography sx={{ whiteSpace: 'pre-line', fontSize: 14 }}>{m.notes}</Typography></Panel>}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        {dlg?.type === 'validation' && (
          <>
            <DialogTitle>Facture {dlg.f.numero} — validation client</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
              <TextField type="date" label="Validation DAPA" InputLabelProps={{ shrink: true }} value={dlg.validation_dapa} onChange={(e) => setDlg({ ...dlg, validation_dapa: e.target.value })} />
              <TextField type="date" label="Validation comptabilité du client" InputLabelProps={{ shrink: true }} value={dlg.validation_compta}
                helperText={`Échéance = cette date + ${m.delai_paiement_jours} jours`} onChange={(e) => setDlg({ ...dlg, validation_compta: e.target.value })} />
              <Button sx={{ justifySelf: 'start' }} onClick={() => navigate(`/factures/${dlg.f.id}`)}>Ouvrir la facture (paiements)</Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" disabled={busy} onClick={() => run(() => marcheAPI.validation(dlg.f.id, { validation_dapa: dlg.validation_dapa, validation_compta: dlg.validation_compta }))}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
        {dlg?.type === 'reprise' && (
          <>
            <DialogTitle>{dlg.f ? `${EXT_TYPES[dlg.f.type]} ${dlg.f.numero}` : 'Facture antérieure à l\'ERP, avoir ou écart'}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <TextField select fullWidth size="small" label="Type" value={dlg.type_ext || dlg.f?.type || 'FACTURE'} onChange={(e) => setDlg({ ...dlg, type_ext: e.target.value })}>
                    {Object.entries(EXT_TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={7}><TextField fullWidth size="small" label="Numéro" value={dlg.numero} onChange={(e) => setDlg({ ...dlg, numero: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Montant HT (F)" value={dlg.montant_ht} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setDlg({ ...dlg, montant_ht: e.target.value.replace(/[^\d-]/g, '') })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Date d'enlèvement" InputLabelProps={{ shrink: true }} value={dlg.date_enlevement} onChange={(e) => setDlg({ ...dlg, date_enlevement: e.target.value })} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Validation DAPA" InputLabelProps={{ shrink: true }} value={dlg.validation_dapa} onChange={(e) => setDlg({ ...dlg, validation_dapa: e.target.value })} /></Grid>
                <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Validation comptabilité" InputLabelProps={{ shrink: true }} value={dlg.validation_compta} onChange={(e) => setDlg({ ...dlg, validation_compta: e.target.value })} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Payée le" InputLabelProps={{ shrink: true }} value={dlg.date_paiement}
                  helperText="Vide : non payée" onChange={(e) => setDlg({ ...dlg, date_paiement: e.target.value })} /></Grid>
                <Grid item xs={12}><TextField fullWidth size="small" multiline minRows={2} label="Note" value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} /></Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {dlg.f && m.admin && <Button color="error" sx={{ mr: 'auto' }} onClick={() => window.confirm('Supprimer cette ligne ?') && run(() => marcheAPI.supprimerReprise(dlg.f.id))}>Supprimer</Button>}
              <Button onClick={() => setDlg(null)}>Annuler</Button>
              <Button variant="contained" disabled={busy || !dlg.numero || !dlg.montant_ht}
                onClick={() => {
                  const body = { type: dlg.type_ext || dlg.f?.type || 'FACTURE', numero: dlg.numero, montant_ht: dlg.montant_ht, date_enlevement: dlg.date_enlevement,
                    validation_dapa: dlg.validation_dapa, validation_compta: dlg.validation_compta, date_paiement: dlg.date_paiement, note: dlg.note };
                  run(() => (dlg.f ? marcheAPI.majReprise(dlg.f.id, body) : marcheAPI.ajouterReprise(id, body)));
                }}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Layout>
  );
};

export default Marche;
